import { useTranslation } from '@papan/i18n'

/**
 * Shown when `useMyCapabilities().unavailable` is set — the capability fetch
 * FAILED, so every `can()` is false because the answer is unknown, not because
 * permission was refused (#341).
 *
 * Without this the two states are indistinguishable on screen: an org owner
 * whose fetch failed sees admin controls quietly missing and concludes their
 * permissions were changed. Saying "we couldn't check" is both truthful and
 * actionable, where silence is neither.
 *
 * Deliberately a NOTICE, not a replacement for the section. The surrounding UI
 * still fails closed on `can()`, so it is safe to render; the user simply learns
 * why it looks the way it does, and gets a retry.
 */
interface Props {
  /** Re-run the capability query (the hook's `retry`). */
  onRetry: () => void
  /** What the caller is gating, e.g. "member management". Keeps copy specific. */
  subject?: string
}

export function CapabilitiesUnavailableNotice({ onRetry, subject }: Props) {
  const { t } = useTranslation('core-ui')
  const message = subject
    ? t('capabilitiesNotice.messageWithSubject', { subject })
    : t('capabilitiesNotice.message')

  return (
    <div
      role="status"
      className="mb-3 flex items-start justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2"
    >
      <p className="text-sm themed-text-secondary">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 text-sm font-medium text-blue-400 hover:text-blue-300"
      >
        {t('capabilitiesNotice.retry')}
      </button>
    </div>
  )
}
